import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { message, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { RefreshCw01, XClose } from '@insight-health-ai/roseate/icons';

const getCurrentVersion = () => {
  const metaTag = document.querySelector('meta[name="app-version"]');
  return metaTag?.getAttribute('content') || null;
};

const fetchServerVersion = async (): Promise<string | null> => {
  const response = await fetch(window.location.origin, {
    cache: 'no-cache',
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  });

  const html = await response.text();

  const serverVersionMatch = html.match(
    /name="app-version"\s+content="([^"]+)"/
  );

  return serverVersionMatch?.[1] || null;
};

const showReloadMessage = () => {
  // Only show message if VITE_ENFORCE_USER_SCREEN_REFRESH is true

  if (import.meta.env.VITE_ENFORCE_USER_SCREEN_REFRESH !== 'true') {
    return;
  }

  const messageKey = 'version-update-message';

  const handleRefresh = () => {
    // Update the message content to show loading state
    message.info({
      content: renderMessage(true),
      duration: 0,
      key: messageKey,
      icon: <InfoCircleOutlined className="text-primary" />,
    });

    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const renderMessage = (isLoading: boolean) => (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}
    >
      <span>New version available! Reload to get updates.</span>
      <Button
        type="primary"
        size="small"
        onClick={handleRefresh}
        style={{ marginLeft: '10px' }}
        loading={isLoading}
        disabled={isLoading}
        className="notification-primary-btn py-3 px-2 flex items-center justify-center"
      >
        {!isLoading ? <RefreshCw01 className="!text-[#fff]" /> : null}
        {isLoading ? 'Reloading...' : 'Reload Now'}
      </Button>
      <div className="flex items-center justify-center ml-2 size-4 cursor-pointer">
        <XClose
          className="cursor-pointer hover:text-gray-800"
          onClick={() => message.destroy(messageKey)}
        />
      </div>
    </div>
  );

  message.info({
    content: renderMessage(false),
    duration: 0,
    key: messageKey,
    icon: <InfoCircleOutlined style={{ color: '#00d090' }} />,
  });
};

export const useVersionCheck = () => {
  const currentVersion = getCurrentVersion();
  const [hasShownNotification, setHasShownNotification] = useState(false);

  const {
    data: serverVersion,
    isError,
    error,
  } = useQuery({
    queryKey: ['version-check'],
    queryFn: fetchServerVersion,
    enabled: !!currentVersion,
    refetchInterval: 10 * 60 * 1000, // Check every 10 minutes
    refetchOnWindowFocus: true,
    refetchIntervalInBackground: false,
    retry: (failureCount) => {
      return failureCount < 2;
    },
    staleTime: 0,
  });

  // Check for version mismatch and show notification only once
  useEffect(() => {
    if (serverVersion && currentVersion) {
      if (currentVersion !== serverVersion && !hasShownNotification) {
        showReloadMessage();
        setHasShownNotification(true);
      } else if (currentVersion === serverVersion) {
        // Reset the notification state if versions match again
        setHasShownNotification(false);
      }
    }
  }, [serverVersion, currentVersion, hasShownNotification]);

  useEffect(() => {
    if (isError) {
      console.log('Version check failed:', error);
    }
  }, [isError, error]);

  return {
    currentVersion,
    serverVersion,
    hasUpdate:
      serverVersion && currentVersion && currentVersion !== serverVersion,
    isChecking: !serverVersion && !isError,
    error: isError ? error : null,
  };
};
